import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, Terminal, Trash2, TriangleAlert } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { Connection, CreatedMcpToken, McpServerInfo, McpToken } from '../api/client';
import { Alert, EmptyState, Spinner } from '../components/Feedback';
import { CopyButton } from '../components/CopyButton';
import { OneTimeToken } from '../components/OneTimeToken';
import { useMcpTokens } from '../components/McpTokenProvider';
import { ConnectionScopePicker, SafetyNote, ScopeSummary } from '../components/ScopePicker';

const EXPIRY_CHOICES = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

export function McpTokensPage() {
  const { hasActiveToken, refresh } = useMcpTokens();
  const [tokens, setTokens] = useState<McpToken[] | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [server, setServer] = useState<McpServerInfo | null>(null);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<CreatedMcpToken | null>(null);
  // The plaintext of the token being revealed, valid only for as long as the
  // countdown runs. Cleared by `OneTimeToken` when the timer ends or the panel
  // is dismissed, so nothing downstream keeps a working credential around.
  const [liveToken, setLiveToken] = useState('');

  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [allowPdfGeneration, setAllowPdfGeneration] = useState(false);
  const [allowDocxGeneration, setAllowDocxGeneration] = useState(false);
  const [allowDatabaseTools, setAllowDatabaseTools] = useState(false);
  const [documentTools, setDocumentTools] = useState({
    pptx: false, txt: false, md: false, html: false, json: false, csv: false,
  });
  // null = every database. The default is deliberately the narrower choice:
  // a token that covers everything is the thing users are trying to avoid.
  const [scope, setScope] = useState<string[] | null>([]);
  const [creating, setCreating] = useState(false);
  const [editingScopeFor, setEditingScopeFor] = useState('');
  const [editScope, setEditScope] = useState<string[] | null>(null);
  const [savingScope, setSavingScope] = useState(false);

  const load = useCallback(() => {
    api.mcp
      .tokens()
      .then(setTokens)
      .catch((e) => setError(e.message));
    api.mcp
      .serverInfo()
      .then(setServer)
      .catch(() => setServer(null));
    api.databases
      .list()
      .then(setConnections)
      .catch(() => setConnections([]));
  }, []);

  useEffect(load, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      const created = await api.mcp.createToken({
        name: name.trim(),
        scope: 'read_only',
        expires_in_days: expiresInDays,
        connection_ids: allowDatabaseTools ? scope : [],
        allow_database_tools: allowDatabaseTools,
        allow_pdf_generation: allowPdfGeneration,
        allow_docx_generation: allowDocxGeneration,
        allow_pptx_generation: documentTools.pptx,
        allow_txt_generation: documentTools.txt,
        allow_md_generation: documentTools.md,
        allow_html_generation: documentTools.html,
        allow_json_generation: documentTools.json,
        allow_csv_generation: documentTools.csv,
      });
      setIssued(created);
      setLiveToken(created.token);
      setName('');
      setScope([]);
      setAllowDatabaseTools(false);
      setAllowPdfGeneration(false);
      setAllowDocxGeneration(false);
      setDocumentTools({ pptx: false, txt: false, md: false, html: false, json: false, csv: false });
      load();
      // The route guard reads the shared state, so it has to be told that the
      // user now has a token; otherwise they stay on this page with no way on.
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The token could not be created.');
    } finally {
      setCreating(false);
    }
  };

  const saveScope = async (token: McpToken) => {
    if (token.allow_database_tools && Array.isArray(editScope) && editScope.length === 0) {
      setError('Select at least one database, or choose all databases.');
      return;
    }
    if (
      Array.isArray(editScope) &&
      editScope.length === 0 &&
      !token.allow_pdf_generation && !token.allow_docx_generation
      && !token.allow_pptx_generation && !token.allow_txt_generation
      && !token.allow_md_generation && !token.allow_html_generation
      && !token.allow_json_generation && !token.allow_csv_generation
    ) {
      setError('This token needs database access because no document generator is selected.');
      return;
    }
    setSavingScope(true);
    setError('');
    try {
      await api.mcp.updateTokenScope(token.id, editScope);
      setEditingScopeFor('');
      load();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : 'The token could not be updated.',
      );
    } finally {
      setSavingScope(false);
    }
  };

  const revoke = async (token: McpToken) => {
    const confirmed = window.confirm(
      `Revoke “${token.name}”?\n\nAny agent using it stops working immediately. This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await api.mcp.revokeToken(token.id);
      load();
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The token could not be revoked.');
    }
  };

  const active = (tokens ?? []).filter((t) => !t.revoked && !t.expired);

  // The server always sends the placeholder form, so substituting the real
  // token is a single replace. Guarded on the timer: once `liveToken` is
  // cleared this falls back to the placeholder rather than keeping the
  // credential embedded in the config on screen.
  const configWithToken =
    server && liveToken
      ? server.config_example.replace('omcp_YOUR_TOKEN', liveToken)
      : (server?.config_example ?? '');

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">AGENT ACCESS</p>
          <h1>MCP tokens</h1>
          <p className="muted">
            A token grants an AI agent access to your selected databases and any document
            generators you enable. Each token carries the permissions of the account that
            created it.
          </p>
        </div>
      </header>

      {error && <Alert tone="error" onDismiss={() => setError('')}>{error}</Alert>}

      {issued && (
        <OneTimeToken
          created={issued}
          onFinished={() => {
            setIssued(null);
            setLiveToken('');
          }}
        />
      )}

      {/*
        Shown whenever nothing can actually be used, whether that is because the
        account has no tokens yet or because every one of them is revoked or
        expired. Gated on `tokens !== null` so the banner does not flash while
        the list is still loading. The wording adapts, since the fix differs:
        a new account only has to create one, whereas revoked tokens also have
        to be swapped into the client config.
      */}
      {tokens !== null && active.length === 0 && (
        <Alert tone="warning">
          <strong>No active token.</strong>{' '}
          {tokens.length === 0
            ? 'No MCP client can reach your databases until you create one.'
            : 'Every token below is revoked or expired, so no MCP client can reach your databases right now.'}{' '}
          Create a token to restore access
          {tokens.length > 0 && (
            <>
              , then update the <code>Authorization</code> header in your client&apos;s
              configuration — the old value stops working immediately
            </>
          )}
          .
        </Alert>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2>Create a token</h2>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="token-name">Label</label>
            <input
              id="token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My laptop"
              maxLength={120}
            />
          </div>
          <div className="field">
            <label htmlFor="token-expiry">Expires</label>
            <select
              id="token-expiry"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
            >
              {EXPIRY_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="muted">
          Tokens are read-only. MCPForge refuses every data-changing statement, so an agent
          cannot modify your data. Generated files are temporary download links, not saved
          into your database or a user folder.
        </p>

        {/*
          The scope picker sits with the other creation fields rather than on its
          own screen: which databases a token may reach is as much a part of
          "what am I creating" as its name and expiry, and splitting it away
          invites people to accept the default without reading it.
        */}
        <div className="field">
          <strong>Tools this token can use</strong>
          <p className="muted">
            Select which tools this token can use. Only selected tools are shown to your AI agent.
          </p>
          <label className="scope-option">
            <input
              type="checkbox"
              checked={allowDatabaseTools}
              onChange={(event) => setAllowDatabaseTools(event.target.checked)}
              disabled={creating}
            />
            <Terminal size={16} />
            <span>
              <strong>Database tools</strong>
              <small>Browse schemas and run read-only queries on selected databases.</small>
            </span>
          </label>
          {allowDatabaseTools && (
            <div className="field">
              <ConnectionScopePicker
                connections={connections}
                value={scope}
                onChange={setScope}
                disabled={creating}
                legend="Select databases for this token"
              />
            </div>
          )}
          <label className="scope-option">
            <input
              type="checkbox"
              checked={allowPdfGeneration}
              onChange={(event) => setAllowPdfGeneration(event.target.checked)}
              disabled={creating}
            />
            <Terminal size={16} />
            <span>
              <strong>PDF generator</strong>
              <small>
                Creates a PDF document and returns a temporary download link.
              </small>
            </span>
          </label>
          {([
            ['pptx', 'PowerPoint generator (PPTX)', 'Builds a presentation from a slide outline.'],
            ['txt', 'Text file generator (TXT)', 'Creates a plain UTF-8 text file.'],
            ['md', 'Markdown generator (MD)', 'Creates a Markdown document.'],
            ['html', 'HTML generator', 'Creates an HTML file from supplied markup.'],
            ['json', 'JSON generator', 'Validates and formats JSON content.'],
            ['csv', 'CSV generator', 'Creates a comma-separated values file.'],
          ] as const).map(([extension, label, description]) => (
            <label className="scope-option" key={extension}>
              <input
                type="checkbox"
                checked={documentTools[extension]}
                onChange={(event) => setDocumentTools((current) => ({ ...current, [extension]: event.target.checked }))}
                disabled={creating}
              />
              <Terminal size={16} />
              <span><strong>{label}</strong><small>{description}</small></span>
            </label>
          ))}
          <label className="scope-option">
            <input
              type="checkbox"
              checked={allowDocxGeneration}
              onChange={(event) => setAllowDocxGeneration(event.target.checked)}
              disabled={creating}
            />
            <Terminal size={16} />
            <span>
              <strong>Word document generator (DOCX)</strong>
              <small>Creates an editable Word document and returns a temporary download link.</small>
            </span>
          </label>
        </div>

        <SafetyNote>
          {allowDatabaseTools
            ? Array.isArray(scope) && scope.length === 0
              ? 'Select at least one database, or choose all databases.'
              : 'Database access is read-only and limited to the databases selected above.'
            : allowPdfGeneration || allowDocxGeneration || Object.values(documentTools).some(Boolean)
              ? 'This token only includes the selected document generators.'
              : 'No tools are selected. Select database tools or a document generator.'}
        </SafetyNote>

        <button
          className="primary"
          onClick={create}
          disabled={
            creating ||
            !name.trim() ||
            (allowDatabaseTools && Array.isArray(scope) && scope.length === 0) ||
            (!allowDatabaseTools && !allowPdfGeneration && !allowDocxGeneration && !Object.values(documentTools).some(Boolean))
          }
        >
          <Plus size={16} /> {creating ? 'Creating…' : 'Create token'}
        </button>
      </section>

      {/*
        Your tokens sits directly under the create form, so the result of
        clicking "Create token" appears immediately below the button rather
        than one screen further down. The client-configuration panel is
        reference material and reads fine at the end of the page.
      */}
      <section className="panel">
        <div className="panel-head">
          <h2>
            <KeyRound size={17} /> Your tokens
          </h2>
          {tokens && <span className="muted">{active.length} active</span>}
        </div>

        {tokens === null ? (
          <Spinner label="Loading tokens" />
        ) : tokens.length === 0 ? (
          <EmptyState
            title="No tokens yet"
            description="Create one to give an AI agent access to your databases."
          />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Databases</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id}>
                    <td>
                      <strong>{token.name}</strong>
                      <br />
                      <code className="muted">…{token.token_hint}</code>
                    </td>
                    <td>
                      {!token.allow_database_tools ? (
                        <span className="muted">Database tools disabled</span>
                      ) : editingScopeFor === token.id ? (
                        <div className="scope-editor">
                          <ConnectionScopePicker
                            connections={connections}
                            value={editScope}
                            onChange={setEditScope}
                            disabled={savingScope}
                            legend="Databases this token can reach"
                          />
                          <div className="scope-editor-actions">
                            <button
                              className="primary"
                              onClick={() => void saveScope(token)}
                              disabled={savingScope}
                            >
                              {savingScope ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              className="ghost"
                              onClick={() => setEditingScopeFor('')}
                              disabled={savingScope}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <ScopeSummary
                          connections={connections}
                          connectionIds={token.connection_ids}
                          allConnections={token.all_connections}
                          onEdit={
                            token.revoked || token.expired
                              ? undefined
                              : () => {
                                  setEditScope(
                                    token.all_connections
                                      ? connections.map((c) => c.id)
                                      : (token.connection_ids ?? []),
                                  );
                                  setEditingScopeFor(token.id);
                                }
                          }
                        />
                      )}
                      {/*
                        The scope is editable in place rather than only at
                        creation. Reissuing a token to change its scope means
                        re-pasting a secret into a client config file, which is
                        the step people quietly skip — which would leave a
                        too-broad token in place.
                      */}
                    </td>
                    <td>
                      {token.revoked ? (
                        <span className="badge badge-muted">Revoked</span>
                      ) : token.expired ? (
                        <span className="badge badge-warn">Expired</span>
                      ) : (
                        <span className="badge badge-ok">Active</span>
                      )}
                      <br />
                      <small className="muted">
                        {token.scope === 'read_only' ? 'read-only' : token.scope}
                        {token.allow_database_tools ? ' · database tools' : ''}
                        {token.allow_pdf_generation ? ' · PDF' : ''}
                        {token.allow_docx_generation ? ' · DOCX' : ''}
                        {token.allow_pptx_generation ? ' · PPTX' : ''}
                        {token.allow_txt_generation ? ' · TXT' : ''}
                        {token.allow_md_generation ? ' · MD' : ''}
                        {token.allow_html_generation ? ' · HTML' : ''}
                        {token.allow_json_generation ? ' · JSON' : ''}
                        {token.allow_csv_generation ? ' · CSV' : ''}
                      </small>
                    </td>
                    <td className="muted">
                      {token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="muted">
                      {token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'Never'}
                      {!token.revoked && !token.expired && !token.last_used_at && (
                        <>
                          <br />
                          <small>no agent has connected yet</small>
                        </>
                      )}
                    </td>
                    <td>
                      {!token.revoked && !token.expired && (
                        <button className="link danger" onClick={() => revoke(token)}>
                          <Trash2 size={14} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {server && (
        <section className="panel">
          <div className="panel-head">
            <h2>
              <Terminal size={17} /> Connect your MCP client
            </h2>
          </div>
          <p className="muted">
            Add this to your client&apos;s MCP configuration. Any MCP client works — the format
            below is the common <code>mcpServers</code> shape; others use an equivalent one.
          </p>

          <div className="field">
            <label>MCP server URL</label>
            <div className="copy-row">
              <code className="mono">{server.server_url}</code>
              <CopyButton value={server.server_url} label="Copy" />
            </div>
          </div>

          <div className="field">
            <label>Configuration</label>
            <div className="copy-row">
              {/*
                While the one-time token is on screen the example is emitted
                ready to paste, with the real credential in place of the
                placeholder. Once the countdown clears it, the placeholder
                comes back -- there is no way to show the value again, and
                leaving it embedded here would keep it on screen forever.
              */}
              <pre className="config">{configWithToken}</pre>
              <CopyButton value={configWithToken} label="Copy config" />
            </div>
          </div>

          {liveToken ? (
            <p className="notice">
              <TriangleAlert size={15} /> This configuration contains the token you just
              created. Copy it now — the value is removed from this page when the timer ends.
            </p>
          ) : (
            <p className="notice">
              <TriangleAlert size={15} /> Replace <code>omcp_YOUR_TOKEN</code> with a token
              from the list above. Each token is shown only once, at the moment it is created.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
